import { useCallback } from 'react';

import redirects from '@zg-rentals/auth-comet/redirects';
import { rentalManagerLoginOptions, rentalManagerStepUpLoginOptions } from '@zg-rentals/login-zillow';
import { applyCobrandedLoginParams, zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';

import { useIsInMobileWebView } from '../../hooks/useIsInMobileWebView';

import { ROUTES } from '../../constants/urls';

import { Auth, hasAuth } from '../../components/AuthProvider';
import { bootstrap } from '../../bootstrap/browser';

import type React from 'react';

const APP_NAME = 'zillow-rental-manager-web';

type RedirectHrefFn = (href: string, params: URLSearchParams) => string;

export type AuthCheckWrapperProps = {
  children: React.ReactNode;
  /**
   * Optional custom redirect when unauthenticated. When provided, skips default logic
   * (__DEV__ → featureFlags, mobile native sign-in, signInPage). Use for page-specific returnUrl.
   */
  redirectHref?: RedirectHrefFn;
  /** Forwarded to Auth.Boundary */
  loading: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  redirecting: React.ReactNode;
  /** Forwarded to Auth.Boundary */
  onError?: React.ReactNode | ((reason: string) => React.ReactNode);
  /** Forwarded to Auth.Boundary */
  onUnauthorized?: React.ReactNode | ((user: unknown) => React.ReactNode);
};

const loginRedirectHref = zillowLoginRedirectHref({
  entryPoint: APP_NAME,
  ...rentalManagerLoginOptions,
});

const stepUpRedirectHref = zillowLoginRedirectHref({
  entryPoint: APP_NAME,
  ...rentalManagerLoginOptions,
  ...rentalManagerStepUpLoginOptions,
});

const mfaRedirectHref: RedirectHrefFn = (href, params) => {
  bootstrap.monitor.count({ name: 'mfa step-up attempt total' }, { name: 'mfa step-up attempt type MfaStepUpAttempt' });

  return stepUpRedirectHref(href, params);
};

const onMfaRedirectAuthorized = () =>
  bootstrap.monitor.count({ name: 'mfa step-up success total' }, { name: 'mfa step-up success type MfaStepUpSuccess' });

/**
 * Wraps children with Auth.Boundary for Next.js app pages. When the user is not authenticated,
 * redirects to a login page. Pass redirectHref to override with a custom URL (e.g. returnUrl only).
 */
const AuthCheckWrapper = ({
  children,
  redirectHref,
  loading,
  redirecting,
  onError,
  onUnauthorized,
}: AuthCheckWrapperProps) => {
  const isInMobileWebView = useIsInMobileWebView();

  const resolveRedirectHref = useCallback<RedirectHrefFn>(
    (href, params) => {
      if (redirectHref) {
        return redirectHref(href, params);
      }

      if (__DEV__) {
        return `/rental-manager${ROUTES.FEATURE_FLAGS}`;
      }

      const manifest = redirects.zillow;

      if (isInMobileWebView && 'nativeAppSignInPage' in manifest) {
        const search = params.size ? `?${params.toString()}` : '';
        const redirectTo = manifest.nativeAppSignInPage?.(href, search);
        if (redirectTo) {
          return redirectTo;
        }
      }

      const originalParams = new URLSearchParams(params);
      params.delete('cobrand');
      params.delete('email');
      const loginHref = loginRedirectHref(href, params);
      return applyCobrandedLoginParams(loginHref, originalParams);
    },
    [redirectHref, isInMobileWebView],
  );

  return (
    <Auth.Boundary
      redirectHref={resolveRedirectHref}
      loading={loading}
      redirecting={redirecting}
      onError={onError}
      onUnauthorized={onUnauthorized}
    >
      <Auth.Boundary
        isAuthorized={hasAuth}
        redirectHref={mfaRedirectHref}
        searchParam={['sp', 'x']}
        loading={loading}
        redirecting={redirecting}
        onError={onError}
        onRedirectAuthorized={onMfaRedirectAuthorized}
        onUnauthorized={onUnauthorized}
      >
        {children}
      </Auth.Boundary>
    </Auth.Boundary>
  );
};

export default AuthCheckWrapper;
